// Supplementary translations for components not yet covered in the main i18n.js dictionary.
// Use: const x = useExtraT();
import { useI18n } from "./i18n";

const EXTRA = {
  fr: {
    common: {
      home: "Accueil",
      zones: "Zones",
      services: "Services",
      careers: "Carrière",
      about: "À propos",
      back: "Retour",
      backHome: "Retour à l'accueil",
      continue: "Continuer",
      sending: "Envoi…",
      loading: "Chargement…",
      send: "Envoyer",
      yes: "Oui",
      no: "Non",
      checkVerify: "Vérifier",
      callBack: "Être rappelé",
      requestQuote: "Demander un devis",
      requestQuoteFree: "Demander un devis gratuit",
      requestQuoteMine: "Demander mon devis gratuit",
      reassureLine: "Devis gratuit sous 24h · Sans engagement · Intervention rapide",
      suisseRomande: "Suisse romande",
      lausanneGeneveSuisse: "Lausanne · Genève · Suisse romande",
      adminLink: "Admin",
      whatsapp: "WhatsApp",
      sendWhatsAppPhotos: "Envoyer mes photos sur WhatsApp",
      contactWhatsApp: "Nous contacter sur WhatsApp",
      discover: "Découvrir",
      discoverArrow: "Découvrir →",
      particular: "Particulier",
      professional: "Professionnel",
      change: "Changer",
      requiredFields: "Merci de remplir les champs obligatoires.",
      emailInvalid: "Email invalide.",
      contactPhoneRequired: "Merci d'indiquer votre téléphone.",
      contactNameRequired: "Merci d'indiquer votre prénom et nom.",
      contactAddressRequired: "Merci d'indiquer l'adresse d'intervention.",
      contactEmailRequired: "L'email est requis pour les professionnels.",
      consentRequired: "Veuillez accepter le traitement de vos données.",
      sendingError: "Erreur lors de l'envoi",
      bookingError: "Erreur",
      decrease: "Diminuer",
      increase: "Augmenter",
    },
    // Service detail page texts
    sp: {
      finalH2a: "Prêt à simplifier votre",
      finalH2b: "?",
      finalSub: "Expliquez-nous votre besoin ou envoyez-nous quelques photos. Notre équipe vous répond gratuitement sous 24 heures.",
      reviewsTitle: "Ce que disent les Romand·e·s qui nous ont fait confiance.",
      reviewsRating: "Avis clients",
      reviewsBadgeTitle: "Équipe pro · Devis 24h",
      zonesTitle: "Mopper, partout en Suisse romande.",
      zonesCheckTitle: "Mopper intervient-il chez vous ?",
      zonesPlaceholder: "Ex. 1003",
      zonesInvalid: "Saisissez un code postal suisse à 4 chiffres.",
      zonesOk: (zip) => `Oui ! Mopper intervient au ${zip}. Demandez votre devis.`,
      zonesKo: (zip) => `Le ${zip} est hors zone Suisse romande. Contactez-nous, on étudie au cas par cas.`,
      faqTitle: "Vos questions, nos réponses.",
      relatedTitle: "Vous pourriez aussi avoir besoin de :",
      tierCta: "Demander une estimation",
      caseCta: "Demander une intervention similaire",
      step4Title: "4 étapes simples, zéro stress.",
      heroH1Suffix: "en Suisse romande",
    },
    // Canton page
    cp: {
      breadcrumbZone: "Zone d'intervention · Suisse romande",
      why: (name) => {
        const prep = (name === "Valais" || name === "Jura") ? `du ${name}` : `de ${name}`;
        return `Pourquoi Mopper dans le canton ${prep} ?`;
      },
      cities: "Principales villes desservies",
      allServices: (name) => {
        const prep = (name === "Valais" || name === "Jura") ? `du ${name}` : `de ${name}`;
        return `Tous les services Mopper disponibles dans le canton ${prep}.`;
      },
      requestTitle: (name) => {
        const prep = (name === "Valais" || name === "Jura") ? `du ${name}` : `de ${name}`;
        return `Une demande dans le canton ${prep} ?`;
      },
      requestSub: "Devis gratuit sous 24h · Sans engagement · Intervention rapide",
    },
    // About page
    about: {
      kickerStory: "L'histoire Mopper",
      h1: "Mopper, l'entreprise qui simplifie vos espaces et vos transitions.",
      heroSub: "On s'occupe de tout ce qui vous encombre. Déménagement, débarras, nettoyage fin de bail et transport de meubles : Mopper accompagne particuliers et professionnels en Suisse romande, du premier carton au dernier coup de propre.",
      mission: "Notre mission",
      missionH2: "Vous soulager. Vous faire gagner du temps. Vous simplifier la vie.",
      missionIntro: "Mopper existe pour soulager nos clients des tâches lourdes, stressantes et chronophages liées à leur logement, leurs meubles ou leurs espaces encombrés.",
      missionItems: [
        "Vous faire gagner du temps",
        "Réduire la charge mentale",
        "Simplifier les déménagements",
        "Vider les espaces encombrés",
        "Remettre de l'ordre",
        "Gérer l'évacuation et le recyclage",
        "Un seul interlocuteur fiable",
        "Une équipe formée et équipée",
      ],
      diffKicker: "Ce qui nous différencie",
      diffH2: "Une entreprise multiservices, pensée comme un seul interlocuteur.",
      diffCards: [
        { title: "Un seul prestataire", desc: "Plusieurs besoins, une seule équipe. On orchestre, vous respirez." },
        { title: "Approche moderne", desc: "Devis sous 24h, photos par WhatsApp, intervention organisée. Pas de bricolage." },
        { title: "Équipe réactive", desc: "Intervention possible en quelques jours, planning flexible." },
        { title: "Service clair et facile", desc: "Pas de jargon, pas de surprise. Vous savez ce qui est inclus." },
        { title: "Logique responsable", desc: "Tri systématique, dons, recyclage avant déchèterie quand c'est possible." },
        { title: "Expérience premium", desc: "Du premier contact à la fin de l'intervention, soin et professionnalisme à chaque étape." },
      ],
      servicesKicker: "Nos services",
      servicesH2: "Cinq services, une seule équipe.",
      forWhoKicker: "Nos clients",
      forWhoH2: "Pour qui travaillons-nous ?",
      particularsTitle: "Particuliers",
      particularsItems: [
        "Déménagement d'appartement ou de maison",
        "Cave, grenier ou garage à vider",
        "Succession et débarras après décès",
        "Séparation, divorce, recomposition familiale",
        "Arrivée d'un enfant, agrandissement",
        "Logement encombré à désencombrer",
        "Nettoyage avant ou après état des lieux",
        "Transport de meubles",
      ],
      prosTitle: "Professionnels",
      prosItems: [
        "Régies immobilières et gérances",
        "Entreprises, bureaux, commerces",
        "Indépendants et professions libérales",
        "Courtiers et agents immobiliers",
        "Locaux à vider après bail",
        "Mobilier professionnel à transporter",
        "Nettoyage après départ de locataire",
        "Préparation de locaux avant relocation",
      ],
      methodKicker: "Comment ça se passe ?",
      methodH2: "Quatre étapes simples, pas une de plus.",
      methodSteps: [
        { title: "Vous nous contactez", desc: "Formulaire en ligne, email ou WhatsApp. Pas de jargon, on parle simple." },
        { title: "Vous envoyez vos infos", desc: "Quelques photos, une vidéo ou une courte description suffisent." },
        { title: "On vous envoie un devis clair", desc: "Sous 24h. Prix transparent, prestations détaillées, sans engagement." },
        { title: "L'équipe Mopper intervient", desc: "Vider, transporter, ranger, nettoyer, recycler. Vous respirez." },
      ],
      stepWord: "Étape",
      pledgesKicker: "Nos engagements",
      pledgesH2: "Dix promesses concrètes, à chaque intervention.",
      pledges: [
        "Ponctualité respectée",
        "Soin des meubles et des objets",
        "Devis clair et transparent",
        "Intervention organisée",
        "Communication simple et directe",
        "Respect total du client",
        "Tri et recyclage quand possible",
        "Discrétion et confidentialité",
        "Travail propre et soigné",
        "Satisfaction garantie",
      ],
      ecoBadge: "Engagement responsable",
      ecoH2: "Limiter le gaspillage, valoriser ce qui peut l'être.",
      ecoBody: "Lorsque c'est possible, Mopper oriente les objets vers le don (Caritas, EMMAÜS, associations locales), la revalorisation ou les filières de recyclage adaptées. La déchèterie n'est qu'un dernier recours.",
      ecoSmall: "Nous restons réalistes : tout ne peut pas être sauvé. Mais quand un objet a encore une vie devant lui, on prend le temps de lui en donner une.",
      ecoItems: [
        "Tri systématique avant évacuation",
        "Partenariats avec associations suisses",
        "Valorisation électroménager et matelas",
        "Photos de don sur demande",
      ],
      zonesKicker: "Zones d'intervention",
      zonesH2: "Toute la Suisse romande.",
      zonesBody: "Mopper intervient en Suisse romande, notamment dans le canton de Vaud, à Lausanne, Morges, Renens, Bussigny, Nyon, Vevey, Montreux, Yverdon-les-Bains, Genève et Fribourg. Notre équipe peut également intervenir à Neuchâtel, en Valais et dans le Jura sur demande.",
      trustH2: "Pourquoi nos clients nous font confiance.",
      trustItems: ["Suisse romande", "Devis gratuit", "WhatsApp direct", "Intervention rapide", "Services combinables", "Équipe pro"],
      trustNote: "4.4/5 · +250 clients satisfaits",
      finalH2: ["Un espace à vider, des meubles à transporter, un déménagement à organiser ?", "Mopper s'en occupe."],
    },
    // Quote form (devis)
    qf: {
      sectionService: "01 · Service",
      sectionContact: "02 · Vos coordonnées",
      sectionSummary: "03 · Récapitulatif",
      pickService: "Quel service vous intéresse ?",
      pickServiceSub: "Sélectionnez votre besoin principal. Vous pourrez préciser tout le reste juste après.",
      contactTitle: "On a presque fini.",
      contactSub: "Quelques infos pour vous envoyer un devis personnalisé.",
      summaryTitle: "Confirmez et envoyez.",
      summarySub: "On revient vers vous avec un devis sous 24h.",
      youAre: "Vous êtes",
      when: "Quand souhaitez-vous être servi ?",
      precision: "Une précision ?",
      precisionHint: "Photos, contraintes, accès difficile, étage sans ascenseur…",
      precisionPh: "Décrivez ce qui pourrait nous aider à mieux estimer.",
      changeService: "Changer de service",
      thanks: "Merci, c'est noté !",
      thanksSub: "Un email de confirmation arrive à",
      thanksSub2: ". Notre équipe revient vers vous avec un devis sous 24h.",
      thanksHome: "Retour à l'accueil",
      firstName: "Prénom",
      lastName: "Nom",
      companyName: "Nom de l'entreprise",
      companyNamePh: "Ex : Mopper SA",
      phone: "Téléphone",
      email: "Email",
      address: "Adresse d'intervention",
      addressPh: "Rue, n°, complément",
      postal: "Code postal",
      date: "Date souhaitée",
      dateHint: "Une date approximative suffit, on s'adapte.",
      datePh: "Sélectionner une date",
      choosePh: "Sélectionnez…",
      slot: "Créneau préféré",
      noteLabel: "Note",
      summaryService: "Service",
      summaryProfile: "Profil",
      summaryContact: "Contact",
      summaryAddress: "Adresse",
      summaryDate: "Date",
      summarySlot: "Créneau",
      summaryWhen: "Quand",
      services: {
        move: { label: "Déménagement", cta: "Demander mon déménagement" },
        clear: { label: "Débarras", cta: "Demander mon débarras" },
        declutter: { label: "Désencombrement", cta: "Demander un accompagnement" },
        organize: { label: "Home Organizing", cta: "Demander une organisation" },
        clean: { label: "Nettoyage", cta: "Réserver mon nettoyage" },
        recycle: { label: "Recyclage", cta: "Demander un enlèvement" },
        assembly: { label: "Montage de meubles", cta: "Demander un montage" },
        transport: { label: "Transport de meubles", cta: "Demander un transport" },
        full: { label: "Offre complète", cta: "Tout déléguer à Mopper" },
      },
      slots: {
        morning: { label: "Matin", hint: "8h–12h" },
        afternoon: { label: "Après-midi", hint: "12h–17h" },
        evening: { label: "Soir", hint: "17h–20h" },
        any: { label: "Peu importe", hint: "On s'adapte" },
      },
      urgency: {
        asap: { label: "Dès que possible", hint: "Sous 48–72h" },
        soon: { label: "Dans la semaine" },
        month: { label: "Ce mois-ci" },
        flex: { label: "Flexible" },
      },
      clientType: { particulier: "Particulier", professionnel: "Professionnel" },
      titles: {
        move: "Parlez-nous de votre déménagement.",
        transport: "Que faut-il transporter ?",
        clear: "Quel espace faut-il vider ?",
        declutter: "On vous accompagne dans le tri.",
        organize: "Quelle pièce voulez-vous organiser ?",
        clean: "Détails de votre nettoyage.",
        recycle: "Que faut-il enlever et recycler ?",
        assembly: "On monte ou on démonte ?",
        full: "Composez votre offre Mopper.",
        default: "Détails de votre demande.",
      },
      subs: {
        move: "Quelques infos clés pour estimer rapidement votre déménagement.",
        transport: "On adapte le véhicule et l'équipage à vos meubles.",
        clear: "On vide, on trie, on évacue. Donnez-nous les bonnes infos.",
        declutter: "Sans pression, à votre rythme. On vous aide à décider.",
        organize: "Esthétique, pratique, durable.",
        clean: "Pour un devis précis, dites-nous ce qu'il faut faire briller.",
        recycle: "On oriente vers le bon canal : don, recyclage, déchèterie.",
        assembly: "IKEA et autres marques. Pré ou post-déménagement.",
        full: "Cochez tout ce dont vous avez besoin, on synchronise.",
      },
      f: {
        addrFrom: "Adresse de départ",
        addrTo: "Adresse d'arrivée",
        addrPh: "Rue, ville, code postal",
        rooms: "Pièces",
        surface: "Surface (m²)",
        floorFrom: "Étage départ",
        floorTo: "Étage arrivée",
        liftFrom: "Ascenseur départ",
        liftTo: "Ascenseur arrivée",
        addNeeds: "Besoins additionnels",
        selectMultiple: "Vous pouvez sélectionner plusieurs options.",
        specifyOptional: "Précisez si nécessaire (facultatif)",
        needBoxes: "Cartons fournis",
        needDis: "Démontage / remontage de meubles",
        needCleaning: "Nettoyage de l'ancien logement",
        itemTr: "Objet à transporter",
        itemTrPh: "Canapé 3 places, armoire, frigo…",
        dim: "Dimensions approximatives",
        dimPh: "L × l × H en cm",
        depart: "Départ",
        arrive: "Arrivée",
        addr: "Adresse",
        disReq: "Démontage / remontage requis ?",
        spaceType: "Type d'espace",
        spaces: { cave: "Cave", grenier: "Grenier", garage: "Garage", appartement: "Appartement", maison: "Maison", bureau: "Bureau / local" },
        surfApprox: "Surface approx. (m²)",
        volume: "Volume (m³)",
        volApprox: "Volume approx. (m³)",
        floor: "Étage",
        liftAvail: "Ascenseur disponible ?",
        precisions: "Précisions",
        heavy: "Objets lourds (piano, coffre-fort…)",
        cleanAfter: "Nettoyage après débarras",
        targetRoom: "Pièce(s) concernée(s)",
        targetRoomPh: "Dressing, cuisine, garage…",
        goal: "Objectif principal",
        goalPh: "Réduire le volume, libérer de la place, repartir à zéro…",
        roomOrg: "Pièce à organiser",
        roomOrgPh: "Dressing, cuisine, buanderie…",
        goalOrg: "Objectif",
        goalOrgPh: "Esthétique, gain de place, accessibilité…",
        needStorage: "Achat de solutions de rangement",
        needDecFirst: "Désencombrement préalable",
        cleanType: "Type de nettoyage",
        cleanTypes: { fin_bail: "Fin de bail", apres_demenagement: "Après déménagement", apres_debarras: "Après débarras", regulier: "Régulier" },
        nbRooms: "Nombre de pièces",
        options: "Options",
        winInc: "Vitres incluses",
        appInc: "Électroménager (four, frigo…)",
        finalLease: "Présence à l'état des lieux",
        recItems: "Type d'objets à recycler",
        recItemsPh: "Électroménager, mobilier, électronique, carton…",
        asbKind: "Type d'intervention",
        asbKinds: { assembly: "Montage", disassembly: "Démontage", both: "Démontage + remontage" },
        brand: "Marque principale",
        brands: { ikea: "IKEA", autre: "Autre", mix: "Mixte" },
        nbFurn: "Nombre de meubles",
        fullSubsLabel: "Quels services souhaitez-vous inclure ?",
        fullHint: "On combine tout en un seul devis.",
      },
      contactFields: {
        firstPh: "",
        phonePh: "+41 79 000 00 00",
        emailPh: "vous@email.ch",
        postalPh: "1003",
      },
      keys: {
        depart: "Départ",
        arrive: "Arrivée",
        rooms: "Pièces",
        surface: "Surface",
        volume: "Volume",
        floorFrom: "Étage départ",
        options: "Options",
        boxes: "Cartons",
        disassembly: "Démontage",
        cleaning: "Nettoyage",
        cleanAfter: "Nettoyage après",
        heavy: "Objets lourds",
        item: "Objet",
        dim: "Dimensions",
        space: "Espace",
        floor: "Étage",
        precisions: "Précisions",
        room: "Pièce",
        goal: "Objectif",
        purchases: "Achat rangements",
        decFirst: "Désencombrement préalable",
        type: "Type",
        win: "Vitres",
        appl: "Électroménager",
        finalLease: "État des lieux",
        items: "Objets",
        brand: "Marque",
        furn: "Meubles",
        included: "Services inclus",
      },
    },
    job: {
      notFound: "Offre introuvable",
      notFoundSub: "Cette offre n'est plus disponible.",
      seeAll: "Voir toutes les offres",
      openBadge: "Recrutement ouvert",
      closedBadge: "Candidatures closes",
      urgent: "Urgent",
      positions: (n) => `${n} postes`,
      start: "Début :",
      applyOffer: "Postuler à cette offre",
      applyShort: "Postuler",
      description: "Description",
      missions: "Missions principales",
      profile: "Profil recherché",
      skills: "Compétences",
      benefits: "Avantages proposés",
      additionalInfo: "Informations complémentaires",
      brief: "En bref",
      contract: "Contrat",
      rate: "Taux",
      place: "Lieu",
      startK: "Début",
      deadline: "Limite candidature",
      schedule: "Horaires",
      weekend: "Week-end",
      evening: "Soir",
      experience: "Expérience",
      education: "Formation",
      license: "Permis",
      languages: "Langues",
      application: "Candidature",
      applyHere: "Postuler à cette offre",
      mandatory: "Tous les champs marqués d'un astérisque sont obligatoires. Réponse sous 5 jours ouvrés.",
      sent: "Candidature envoyée !",
      sentSub: "Merci pour votre intérêt. Nous revenons vers vous très rapidement.",
      seeOtherJobs: "Voir les autres offres",
      msgCoverLetter: "Message / lettre de motivation",
      msgPh: "Présentez-vous en quelques lignes.",
      docsRequested: "Documents demandés",
      docsHint: "Collez un lien public (Google Drive, Dropbox, OneDrive). Astuce : activez le partage avant de copier le lien.",
      questions: "Questions",
      customDocLabel: "Document personnalisé",
      consent: "J'accepte que Mopper traite mes informations pour les besoins de ce recrutement. Conservées 12 mois max, suppression sur demande à hello@gomopper.ch.",
      submitApp: "Envoyer ma candidature",
      docRequired: (l) => `Le document ${l} est requis (lien public).`,
      qRequired: (t) => `La question "${t}" est requise.`,
      choose: "— choisir —",
      docLabels: { cv: "CV", motivation: "Lettre de motivation", certificates: "Certificats de travail", driving_license: "Permis de conduire", work_permit: "Autorisation de travail", diplomas: "Diplômes", custom: "Document" },
      featured: "En avant",
      city: "Ville",
    },
    booking: {
      mopperBrand: "Mopper · Devis",
      operators: "Intervenants",
      estimateTTC: "Estimation TTC",
      vatLine: "TVA suisse 8.1% incluse. Sans engagement.",
      custom: "Personnalisé",
      personsShort: "pers",
      personsLabel: (n) => `${n} pers.`,
    },
    espace: {
      quote: "Devis",
      quoteIn24h: "Devis sous 24h",
      reviewing: "À l'étude",
      quoted: "Devis envoyé",
      confirmed: "Confirmé",
      completed: "Terminé",
      cancelled: "Annulé",
    },
    wa: {
      reply: "Réponse sous quelques minutes",
      greet: "Bonjour 👋 Une question ou besoin d'un devis ?",
      sub: "Envoyez-nous quelques photos ou décrivez votre besoin sur WhatsApp.",
      start: "Démarrer la conversation",
      close: "Fermer",
      open: "Ouvrir le chat WhatsApp",
      closeAria: "Fermer le chat WhatsApp",
    },
    days: ["L", "M", "M", "J", "V", "S", "D"],
  },
  de: {
    common: {
      home: "Startseite",
      zones: "Gebiete",
      services: "Leistungen",
      careers: "Karriere",
      about: "Über uns",
      back: "Zurück",
      backHome: "Zur Startseite",
      continue: "Weiter",
      sending: "Wird gesendet…",
      loading: "Lädt…",
      send: "Senden",
      yes: "Ja",
      no: "Nein",
      checkVerify: "Prüfen",
      callBack: "Rückruf",
      requestQuote: "Angebot anfordern",
      requestQuoteFree: "Gratis-Angebot anfordern",
      requestQuoteMine: "Mein Gratis-Angebot anfordern",
      reassureLine: "Gratis-Angebot in 24h · Unverbindlich · Schneller Einsatz",
      suisseRomande: "Westschweiz",
      lausanneGeneveSuisse: "Lausanne · Genf · Westschweiz",
      adminLink: "Admin",
      whatsapp: "WhatsApp",
      sendWhatsAppPhotos: "Fotos per WhatsApp senden",
      contactWhatsApp: "Kontakt per WhatsApp",
      discover: "Entdecken",
      discoverArrow: "Entdecken →",
      particular: "Privatkunde",
      professional: "Geschäftskunde",
      change: "Ändern",
      requiredFields: "Bitte füllen Sie die Pflichtfelder aus.",
      emailInvalid: "Ungültige E-Mail.",
      contactPhoneRequired: "Bitte geben Sie Ihre Telefonnummer an.",
      contactNameRequired: "Bitte geben Sie Vor- und Nachname an.",
      contactAddressRequired: "Bitte geben Sie die Einsatzadresse an.",
      contactEmailRequired: "E-Mail ist für Geschäftskunden erforderlich.",
      consentRequired: "Bitte stimmen Sie der Datenverarbeitung zu.",
      sendingError: "Sendefehler",
      bookingError: "Fehler",
      decrease: "Verringern",
      increase: "Erhöhen",
    },
    sp: {
      finalH2a: "Bereit, Ihre",
      finalH2b: "zu vereinfachen?",
      finalSub: "Erklären Sie uns Ihren Bedarf oder senden Sie uns ein paar Fotos. Unser Team antwortet kostenlos innert 24 Stunden.",
      reviewsTitle: "Was Westschweizer Kundinnen und Kunden sagen, die uns vertraut haben.",
      reviewsRating: "Kundenbewertungen",
      reviewsBadgeTitle: "Profi-Team · Angebot 24h",
      zonesTitle: "Mopper, in der ganzen Westschweiz.",
      zonesCheckTitle: "Ist Mopper bei Ihnen aktiv?",
      zonesPlaceholder: "Bsp. 1003",
      zonesInvalid: "Bitte eine vierstellige Schweizer PLZ eingeben.",
      zonesOk: (zip) => `Ja! Mopper ist in ${zip} aktiv. Jetzt Angebot anfordern.`,
      zonesKo: (zip) => `${zip} liegt ausserhalb der Westschweiz. Kontaktieren Sie uns, wir prüfen den Einzelfall.`,
      faqTitle: "Ihre Fragen, unsere Antworten.",
      relatedTitle: "Vielleicht brauchen Sie auch:",
      tierCta: "Schätzung anfragen",
      caseCta: "Ähnlichen Einsatz anfragen",
      step4Title: "4 einfache Schritte, null Stress.",
      heroH1Suffix: "in der Westschweiz",
    },
    cp: {
      breadcrumbZone: "Einsatzgebiet · Westschweiz",
      why: (name) => `Warum Mopper im Kanton ${name}?`,
      cities: "Wichtigste bediente Städte",
      allServices: (name) => `Alle Mopper-Leistungen im Kanton ${name} verfügbar.`,
      requestTitle: (name) => `Eine Anfrage im Kanton ${name}?`,
      requestSub: "Gratis-Angebot in 24h · Unverbindlich · Schneller Einsatz",
    },
    about: {
      kickerStory: "Die Mopper-Geschichte",
      h1: "Mopper, das Unternehmen, das Ihre Räume und Übergänge vereinfacht.",
      heroSub: "Wir kümmern uns um alles, was Sie überfordert. Umzug, Entrümpelung, Endreinigung und Möbeltransport: Mopper begleitet Privatpersonen und Unternehmen in der Westschweiz, vom ersten Karton bis zum letzten Wischen.",
      mission: "Unsere Mission",
      missionH2: "Sie entlasten. Zeit schenken. Das Leben vereinfachen.",
      missionIntro: "Mopper existiert, um Sie von den schweren, stressigen und zeitraubenden Aufgaben rund um Ihre Wohnung, Ihre Möbel oder Ihre überfüllten Räume zu befreien.",
      missionItems: [
        "Zeit gewinnen",
        "Mentale Belastung reduzieren",
        "Umzüge vereinfachen",
        "Überfüllte Räume leeren",
        "Ordnung schaffen",
        "Entsorgung und Recycling verwalten",
        "Ein verlässlicher Ansprechpartner",
        "Ein geschultes und ausgerüstetes Team",
      ],
      diffKicker: "Was uns auszeichnet",
      diffH2: "Ein Multi-Service-Unternehmen, gedacht als ein einziger Ansprechpartner.",
      diffCards: [
        { title: "Ein einziger Anbieter", desc: "Mehrere Bedürfnisse, ein Team. Wir orchestrieren, Sie atmen durch." },
        { title: "Moderner Ansatz", desc: "Angebot in 24h, Fotos via WhatsApp, organisierter Einsatz. Keine Bastellösung." },
        { title: "Reaktives Team", desc: "Einsatz möglich in wenigen Tagen, flexible Planung." },
        { title: "Klarer, einfacher Service", desc: "Kein Fachjargon, keine Überraschungen. Sie wissen, was enthalten ist." },
        { title: "Verantwortliche Logik", desc: "Systematische Sortierung, Spenden, Recycling vor Deponie wann immer möglich." },
        { title: "Premium-Erlebnis", desc: "Vom ersten Kontakt bis zum Ende des Einsatzes — Sorgfalt und Professionalität." },
      ],
      servicesKicker: "Unsere Leistungen",
      servicesH2: "Fünf Leistungen, ein Team.",
      forWhoKicker: "Unsere Kunden",
      forWhoH2: "Für wen arbeiten wir?",
      particularsTitle: "Privatkunden",
      particularsItems: [
        "Wohnungs- oder Hausumzug",
        "Keller, Estrich oder Garage zu leeren",
        "Nachlass und Entrümpelung nach Todesfall",
        "Trennung, Scheidung, Familiengründung",
        "Geburt eines Kindes, Vergrösserung",
        "Überfüllte Wohnung zum Aussortieren",
        "Reinigung vor oder nach Wohnungsabnahme",
        "Möbeltransport",
      ],
      prosTitle: "Unternehmen",
      prosItems: [
        "Liegenschaftsverwaltungen und Hausverwaltungen",
        "Unternehmen, Büros, Geschäfte",
        "Selbständige und freie Berufe",
        "Makler und Immobilienagenten",
        "Räumlichkeiten nach Mietende",
        "Geschäftsmöbel zum Transport",
        "Reinigung nach Auszug eines Mieters",
        "Vorbereitung von Räumen vor Neuvermietung",
      ],
      methodKicker: "Wie läuft es ab?",
      methodH2: "Vier einfache Schritte, kein einziger mehr.",
      methodSteps: [
        { title: "Sie kontaktieren uns", desc: "Online-Formular, E-Mail oder WhatsApp. Kein Fachjargon, wir sprechen Klartext." },
        { title: "Sie senden Ihre Infos", desc: "Ein paar Fotos, ein Video oder eine kurze Beschreibung genügen." },
        { title: "Wir senden ein klares Angebot", desc: "Innert 24h. Transparenter Preis, detaillierte Leistungen, unverbindlich." },
        { title: "Das Mopper-Team rückt aus", desc: "Leeren, transportieren, ordnen, reinigen, recyceln. Sie atmen durch." },
      ],
      stepWord: "Schritt",
      pledgesKicker: "Unsere Verpflichtungen",
      pledgesH2: "Zehn konkrete Versprechen, bei jedem Einsatz.",
      pledges: [
        "Eingehaltene Pünktlichkeit",
        "Sorgfalt mit Möbeln und Objekten",
        "Klares und transparentes Angebot",
        "Organisierter Einsatz",
        "Einfache und direkte Kommunikation",
        "Voller Respekt gegenüber dem Kunden",
        "Sortierung und Recycling wann immer möglich",
        "Diskretion und Vertraulichkeit",
        "Saubere und sorgfältige Arbeit",
        "Zufriedenheitsgarantie",
      ],
      ecoBadge: "Verantwortungsbewusstes Engagement",
      ecoH2: "Verschwendung vermeiden, aufwerten was geht.",
      ecoBody: "Wenn möglich, leitet Mopper Objekte zur Spende (Caritas, EMMAÜS, lokale Vereine), zur Aufwertung oder in passende Recyclingkanäle. Die Deponie ist nur das letzte Mittel.",
      ecoSmall: "Wir bleiben realistisch: Nicht alles kann gerettet werden. Aber wenn ein Objekt noch ein Leben vor sich hat, nehmen wir uns die Zeit, es ihm zu geben.",
      ecoItems: [
        "Systematische Sortierung vor Entsorgung",
        "Partnerschaften mit Schweizer Vereinen",
        "Verwertung von Haushaltsgeräten und Matratzen",
        "Spendenfotos auf Anfrage",
      ],
      zonesKicker: "Einsatzgebiete",
      zonesH2: "Die ganze Westschweiz.",
      zonesBody: "Mopper ist in der Westschweiz aktiv, insbesondere im Kanton Waadt, in Lausanne, Morges, Renens, Bussigny, Nyon, Vevey, Montreux, Yverdon-les-Bains, Genf und Freiburg. Unser Team kann auf Anfrage auch in Neuenburg, im Wallis und im Jura tätig werden.",
      trustH2: "Warum unsere Kunden uns vertrauen.",
      trustItems: ["Westschweiz", "Gratis-Angebot", "WhatsApp direkt", "Schneller Einsatz", "Kombinierbare Leistungen", "Profi-Team"],
      trustNote: "4.4/5 · +250 zufriedene Kunden",
      finalH2: ["Ein Raum zum Leeren, Möbel zum Transportieren, ein Umzug zu organisieren?", "Mopper kümmert sich darum."],
    },
    qf: {
      sectionService: "01 · Leistung",
      sectionContact: "02 · Ihre Kontaktdaten",
      sectionSummary: "03 · Zusammenfassung",
      pickService: "Welche Leistung interessiert Sie?",
      pickServiceSub: "Wählen Sie Ihren Hauptbedarf. Den Rest präzisieren Sie gleich danach.",
      contactTitle: "Fast fertig.",
      contactSub: "Ein paar Infos, um Ihnen ein massgeschneidertes Angebot zu senden.",
      summaryTitle: "Bestätigen und senden.",
      summarySub: "Wir melden uns mit einem Angebot innert 24h.",
      youAre: "Sie sind",
      when: "Wann möchten Sie bedient werden?",
      precision: "Eine Präzision?",
      precisionHint: "Fotos, Einschränkungen, schwieriger Zugang, Etage ohne Lift…",
      precisionPh: "Beschreiben Sie, was uns helfen könnte, besser zu schätzen.",
      changeService: "Leistung ändern",
      thanks: "Danke, notiert!",
      thanksSub: "Eine Bestätigungs-E-Mail kommt an",
      thanksSub2: ". Unser Team meldet sich mit einem Angebot innert 24h.",
      thanksHome: "Zur Startseite",
      firstName: "Vorname",
      lastName: "Nachname",
      companyName: "Firmenname",
      companyNamePh: "z. B. Mopper AG",
      phone: "Telefon",
      email: "E-Mail",
      address: "Einsatzadresse",
      addressPh: "Strasse, Nr., Zusatz",
      postal: "PLZ",
      date: "Wunschdatum",
      dateHint: "Ein ungefähres Datum genügt, wir passen uns an.",
      datePh: "Datum auswählen",
      choosePh: "Auswählen…",
      slot: "Bevorzugter Zeitslot",
      noteLabel: "Notiz",
      summaryService: "Leistung",
      summaryProfile: "Profil",
      summaryContact: "Kontakt",
      summaryAddress: "Adresse",
      summaryDate: "Datum",
      summarySlot: "Zeitslot",
      summaryWhen: "Wann",
      services: {
        move: { label: "Umzug", cta: "Umzug anfragen" },
        clear: { label: "Entrümpelung", cta: "Entrümpelung anfragen" },
        declutter: { label: "Aussortieren", cta: "Begleitung anfragen" },
        organize: { label: "Home Organizing", cta: "Organisation anfragen" },
        clean: { label: "Reinigung", cta: "Reinigung buchen" },
        recycle: { label: "Recycling", cta: "Abholung anfragen" },
        assembly: { label: "Möbelmontage", cta: "Montage anfragen" },
        transport: { label: "Möbeltransport", cta: "Transport anfragen" },
        full: { label: "Komplettangebot", cta: "Alles an Mopper delegieren" },
      },
      slots: {
        morning: { label: "Morgen", hint: "8–12 Uhr" },
        afternoon: { label: "Nachmittag", hint: "12–17 Uhr" },
        evening: { label: "Abend", hint: "17–20 Uhr" },
        any: { label: "Egal", hint: "Wir passen uns an" },
      },
      urgency: {
        asap: { label: "So schnell wie möglich", hint: "Innert 48–72h" },
        soon: { label: "In dieser Woche" },
        month: { label: "Diesen Monat" },
        flex: { label: "Flexibel" },
      },
      clientType: { particulier: "Privat", professionnel: "Geschäft" },
      titles: {
        move: "Erzählen Sie uns von Ihrem Umzug.",
        transport: "Was muss transportiert werden?",
        clear: "Welcher Raum muss geleert werden?",
        declutter: "Wir begleiten Sie beim Sortieren.",
        organize: "Welchen Raum möchten Sie organisieren?",
        clean: "Details Ihrer Reinigung.",
        recycle: "Was muss abgeholt und recycelt werden?",
        assembly: "Montage oder Demontage?",
        full: "Stellen Sie Ihr Mopper-Angebot zusammen.",
        default: "Details Ihrer Anfrage.",
      },
      subs: {
        move: "Ein paar Schlüsselinfos, um Ihren Umzug schnell zu schätzen.",
        transport: "Wir passen Fahrzeug und Team an Ihre Möbel an.",
        clear: "Wir leeren, sortieren, entsorgen. Geben Sie uns die richtigen Infos.",
        declutter: "Ohne Druck, in Ihrem Tempo. Wir helfen Ihnen zu entscheiden.",
        organize: "Ästhetisch, praktisch, nachhaltig.",
        clean: "Für ein präzises Angebot sagen Sie uns, was glänzen soll.",
        recycle: "Wir leiten in den richtigen Kanal: Spende, Recycling, Deponie.",
        assembly: "IKEA und andere Marken. Vor oder nach Umzug.",
        full: "Kreuzen Sie an, was Sie brauchen — wir synchronisieren.",
      },
      f: {
        addrFrom: "Abholadresse",
        addrTo: "Zieladresse",
        addrPh: "Strasse, Stadt, PLZ",
        rooms: "Räume",
        surface: "Fläche (m²)",
        floorFrom: "Etage Abholung",
        floorTo: "Etage Ziel",
        liftFrom: "Lift Abholung",
        liftTo: "Lift Ziel",
        addNeeds: "Zusätzliche Bedürfnisse",
        selectMultiple: "Sie können mehrere Optionen auswählen.",
        specifyOptional: "Bei Bedarf angeben (optional)",
        needBoxes: "Kartons geliefert",
        needDis: "Demontage / Montage von Möbeln",
        needCleaning: "Reinigung der alten Wohnung",
        itemTr: "Zu transportierender Gegenstand",
        itemTrPh: "3er-Sofa, Schrank, Kühlschrank…",
        dim: "Ungefähre Masse",
        dimPh: "L × B × H in cm",
        depart: "Abholung",
        arrive: "Ziel",
        addr: "Adresse",
        disReq: "Demontage / Montage erforderlich?",
        spaceType: "Raumtyp",
        spaces: { cave: "Keller", grenier: "Estrich", garage: "Garage", appartement: "Wohnung", maison: "Haus", bureau: "Büro / Lokal" },
        surfApprox: "Ungefähre Fläche (m²)",
        volume: "Volumen (m³)",
        volApprox: "Ungefähres Volumen (m³)",
        floor: "Etage",
        liftAvail: "Lift verfügbar?",
        precisions: "Präzisierungen",
        heavy: "Schwere Objekte (Klavier, Tresor…)",
        cleanAfter: "Reinigung nach Entrümpelung",
        targetRoom: "Betroffener Raum/Räume",
        targetRoomPh: "Ankleide, Küche, Garage…",
        goal: "Hauptziel",
        goalPh: "Volumen reduzieren, Platz schaffen, neu anfangen…",
        roomOrg: "Zu organisierender Raum",
        roomOrgPh: "Ankleide, Küche, Waschküche…",
        goalOrg: "Ziel",
        goalOrgPh: "Ästhetik, Platzgewinn, Zugänglichkeit…",
        needStorage: "Kauf von Aufbewahrungslösungen",
        needDecFirst: "Vorgängiges Aussortieren",
        cleanType: "Reinigungstyp",
        cleanTypes: { fin_bail: "Endreinigung", apres_demenagement: "Nach Umzug", apres_debarras: "Nach Entrümpelung", regulier: "Regelmässig" },
        nbRooms: "Anzahl Räume",
        options: "Optionen",
        winInc: "Fenster inklusive",
        appInc: "Geräte (Ofen, Kühlschrank…)",
        finalLease: "Anwesenheit bei Wohnungsabnahme",
        recItems: "Art der zu recycelnden Objekte",
        recItemsPh: "Geräte, Möbel, Elektronik, Karton…",
        asbKind: "Art des Einsatzes",
        asbKinds: { assembly: "Montage", disassembly: "Demontage", both: "Demontage + Montage" },
        brand: "Hauptmarke",
        brands: { ikea: "IKEA", autre: "Andere", mix: "Gemischt" },
        nbFurn: "Anzahl Möbel",
        fullSubsLabel: "Welche Leistungen möchten Sie einschliessen?",
        fullHint: "Wir kombinieren alles in einem Angebot.",
      },
      contactFields: { firstPh: "", phonePh: "+41 79 000 00 00", emailPh: "sie@unternehmen.ch", postalPh: "1003" },
      keys: {
        depart: "Abholung", arrive: "Ziel", rooms: "Räume", surface: "Fläche", volume: "Volumen", floorFrom: "Etage Abholung", floorTo: "Etage Ziel",
        options: "Optionen", boxes: "Kartons", disassembly: "Demontage", cleaning: "Reinigung", cleanAfter: "Reinigung nach", heavy: "Schwere Objekte",
        item: "Gegenstand", dim: "Masse", space: "Raum", floor: "Etage", precisions: "Präzisierungen", room: "Raum", goal: "Ziel",
        purchases: "Kauf Aufbewahrung", decFirst: "Vorgängiges Aussortieren", type: "Typ", win: "Fenster", appl: "Geräte", finalLease: "Wohnungsabnahme",
        items: "Objekte", brand: "Marke", furn: "Möbel", included: "Enthaltene Leistungen",
      },
    },
    job: {
      notFound: "Stelle nicht gefunden",
      notFoundSub: "Diese Stelle ist nicht mehr verfügbar.",
      seeAll: "Alle Stellen ansehen",
      openBadge: "Bewerbungen offen",
      closedBadge: "Bewerbungen geschlossen",
      urgent: "Dringend",
      positions: (n) => `${n} Stellen`,
      start: "Beginn:",
      applyOffer: "Auf diese Stelle bewerben",
      applyShort: "Bewerben",
      description: "Beschreibung",
      missions: "Hauptaufgaben",
      profile: "Gesuchtes Profil",
      skills: "Kompetenzen",
      benefits: "Angebotene Vorteile",
      additionalInfo: "Ergänzende Informationen",
      brief: "Kurzfassung",
      contract: "Vertrag",
      rate: "Pensum",
      place: "Ort",
      startK: "Beginn",
      deadline: "Bewerbungsfrist",
      schedule: "Arbeitszeiten",
      weekend: "Wochenende",
      evening: "Abend",
      experience: "Erfahrung",
      education: "Ausbildung",
      license: "Führerausweis",
      languages: "Sprachen",
      application: "Bewerbung",
      applyHere: "Auf diese Stelle bewerben",
      mandatory: "Alle mit Sternchen markierten Felder sind Pflicht. Antwort innert 5 Werktagen.",
      sent: "Bewerbung gesendet!",
      sentSub: "Danke für Ihr Interesse. Wir melden uns sehr bald.",
      seeOtherJobs: "Andere Stellen ansehen",
      msgCoverLetter: "Nachricht / Anschreiben",
      msgPh: "Stellen Sie sich in wenigen Zeilen vor.",
      docsRequested: "Angeforderte Dokumente",
      docsHint: "Fügen Sie einen öffentlichen Link ein (Google Drive, Dropbox, OneDrive). Tipp: Freigabe vorher aktivieren.",
      questions: "Fragen",
      customDocLabel: "Personalisiertes Dokument",
      consent: "Ich stimme zu, dass Mopper meine Daten zum Zweck dieser Rekrutierung verarbeitet. Aufbewahrung max. 12 Monate, Löschung auf Anfrage an hello@gomopper.ch.",
      submitApp: "Meine Bewerbung senden",
      docRequired: (l) => `Das Dokument ${l} ist erforderlich (öffentlicher Link).`,
      qRequired: (t) => `Die Frage "${t}" ist erforderlich.`,
      choose: "— auswählen —",
      docLabels: { cv: "Lebenslauf", motivation: "Anschreiben", certificates: "Arbeitszeugnisse", driving_license: "Führerausweis", work_permit: "Arbeitsbewilligung", diplomas: "Diplome", custom: "Dokument" },
      featured: "Hervorgehoben",
      city: "Stadt",
    },
    booking: {
      mopperBrand: "Mopper · Angebot",
      operators: "Einsatzkräfte",
      estimateTTC: "Schätzung brutto",
      vatLine: "Schweizer MWST 8.1% inklusive. Unverbindlich.",
      custom: "Individuell",
      personsShort: "Pers",
      personsLabel: (n) => `${n} Pers.`,
    },
    espace: {
      quote: "Angebot",
      quoteIn24h: "Angebot in 24h",
      reviewing: "In Prüfung",
      quoted: "Angebot gesendet",
      confirmed: "Bestätigt",
      completed: "Abgeschlossen",
      cancelled: "Storniert",
    },
    wa: {
      reply: "Antwort innert weniger Minuten",
      greet: "Hallo 👋 Eine Frage oder ein Angebot nötig?",
      sub: "Senden Sie uns ein paar Fotos oder beschreiben Sie Ihren Bedarf via WhatsApp.",
      start: "Gespräch starten",
      close: "Schliessen",
      open: "WhatsApp-Chat öffnen",
      closeAria: "WhatsApp-Chat schliessen",
    },
    days: ["M", "D", "M", "D", "F", "S", "S"],
  },
  en: {
    common: {
      home: "Home",
      zones: "Areas",
      services: "Services",
      careers: "Careers",
      about: "About",
      back: "Back",
      backHome: "Back to home",
      continue: "Continue",
      sending: "Sending…",
      loading: "Loading…",
      send: "Send",
      yes: "Yes",
      no: "No",
      checkVerify: "Check",
      callBack: "Get a callback",
      requestQuote: "Get a quote",
      requestQuoteFree: "Request a free quote",
      requestQuoteMine: "Request my free quote",
      reassureLine: "Free quote within 24h · No commitment · Fast intervention",
      suisseRomande: "French-speaking Switzerland",
      lausanneGeneveSuisse: "Lausanne · Geneva · French-speaking Switzerland",
      adminLink: "Admin",
      whatsapp: "WhatsApp",
      sendWhatsAppPhotos: "Send my photos on WhatsApp",
      contactWhatsApp: "Contact us on WhatsApp",
      discover: "Discover",
      discoverArrow: "Discover →",
      particular: "Residential",
      professional: "Professional",
      change: "Change",
      requiredFields: "Please fill the required fields.",
      emailInvalid: "Invalid email.",
      contactPhoneRequired: "Please provide your phone number.",
      contactNameRequired: "Please provide your first and last name.",
      contactAddressRequired: "Please provide the intervention address.",
      contactEmailRequired: "Email is required for professional clients.",
      consentRequired: "Please agree to the processing of your data.",
      sendingError: "Sending error",
      bookingError: "Error",
      decrease: "Decrease",
      increase: "Increase",
    },
    sp: {
      finalH2a: "Ready to simplify your",
      finalH2b: "?",
      finalSub: "Tell us your needs or send us a few photos. Our team will reply within 24 hours, free of charge.",
      reviewsTitle: "What our French-speaking Swiss customers say about us.",
      reviewsRating: "Customer reviews",
      reviewsBadgeTitle: "Pro team · Quote 24h",
      zonesTitle: "Mopper, throughout French-speaking Switzerland.",
      zonesCheckTitle: "Does Mopper come to your area?",
      zonesPlaceholder: "e.g. 1003",
      zonesInvalid: "Please enter a 4-digit Swiss postal code.",
      zonesOk: (zip) => `Yes! Mopper serves ${zip}. Request your quote.`,
      zonesKo: (zip) => `${zip} is outside French-speaking Switzerland. Contact us — we'll review case by case.`,
      faqTitle: "Your questions, our answers.",
      relatedTitle: "You may also need:",
      tierCta: "Get an estimate",
      caseCta: "Request a similar intervention",
      step4Title: "4 simple steps, zero stress.",
      heroH1Suffix: "in French-speaking Switzerland",
    },
    cp: {
      breadcrumbZone: "Service area · French-speaking Switzerland",
      why: (name) => `Why Mopper in the canton of ${name}?`,
      cities: "Main cities served",
      allServices: (name) => `All Mopper services available in the canton of ${name}.`,
      requestTitle: (name) => `A request in the canton of ${name}?`,
      requestSub: "Free quote within 24h · No commitment · Fast intervention",
    },
    about: {
      kickerStory: "The Mopper story",
      h1: "Mopper, the company that simplifies your spaces and transitions.",
      heroSub: "We handle everything that clutters you. Moving, clear-outs, end-of-lease cleaning and furniture transport: Mopper supports individuals and businesses in French-speaking Switzerland, from the first box to the final polish.",
      mission: "Our mission",
      missionH2: "Relieve you. Save you time. Make life easier.",
      missionIntro: "Mopper exists to relieve our customers of the heavy, stressful and time-consuming tasks related to their homes, furniture or cluttered spaces.",
      missionItems: [
        "Save you time",
        "Reduce mental load",
        "Simplify moves",
        "Empty cluttered spaces",
        "Restore order",
        "Manage disposal and recycling",
        "A single reliable contact",
        "A trained, equipped team",
      ],
      diffKicker: "What sets us apart",
      diffH2: "A multi-service company designed as a single contact.",
      diffCards: [
        { title: "A single provider", desc: "Many needs, one team. We orchestrate, you breathe." },
        { title: "Modern approach", desc: "Quote within 24h, photos via WhatsApp, organized intervention. No hacks." },
        { title: "Reactive team", desc: "Intervention possible within days, flexible schedule." },
        { title: "Clear, easy service", desc: "No jargon, no surprises. You know what's included." },
        { title: "Responsible logic", desc: "Systematic sorting, donations, recycling before landfill whenever possible." },
        { title: "Premium experience", desc: "From first contact to end of intervention — care and professionalism at every step." },
      ],
      servicesKicker: "Our services",
      servicesH2: "Five services, one team.",
      forWhoKicker: "Our clients",
      forWhoH2: "Who do we work for?",
      particularsTitle: "Residential",
      particularsItems: [
        "Apartment or house moves",
        "Cellar, attic or garage to empty",
        "Inheritance and clear-out after death",
        "Separation, divorce, blended family",
        "New child, expansion",
        "Cluttered home to declutter",
        "Cleaning before or after handover",
        "Furniture transport",
      ],
      prosTitle: "Professionals",
      prosItems: [
        "Property managers and agencies",
        "Companies, offices, retail",
        "Freelancers and liberal professions",
        "Brokers and real estate agents",
        "Premises to empty after lease",
        "Professional furniture to transport",
        "Cleaning after tenant departure",
        "Preparation of premises before re-rental",
      ],
      methodKicker: "How it works",
      methodH2: "Four simple steps, no more.",
      methodSteps: [
        { title: "You contact us", desc: "Online form, email or WhatsApp. No jargon, plain talk." },
        { title: "You send your info", desc: "A few photos, a video or a short description are enough." },
        { title: "We send a clear quote", desc: "Within 24h. Transparent price, detailed services, no commitment." },
        { title: "The Mopper team intervenes", desc: "Empty, transport, organize, clean, recycle. You breathe." },
      ],
      stepWord: "Step",
      pledgesKicker: "Our commitments",
      pledgesH2: "Ten concrete promises, at every intervention.",
      pledges: [
        "Punctuality respected",
        "Care of furniture and items",
        "Clear, transparent quote",
        "Organized intervention",
        "Simple and direct communication",
        "Total respect for the client",
        "Sorting and recycling when possible",
        "Discretion and confidentiality",
        "Clean and careful work",
        "Satisfaction guaranteed",
      ],
      ecoBadge: "Responsible commitment",
      ecoH2: "Limit waste, value what can be valued.",
      ecoBody: "Whenever possible, Mopper directs items to donation (Caritas, EMMAÜS, local associations), revaluation or appropriate recycling channels. Landfill is only a last resort.",
      ecoSmall: "We stay realistic: not everything can be saved. But when an item still has a life ahead, we take the time to give it one.",
      ecoItems: [
        "Systematic sorting before disposal",
        "Partnerships with Swiss charities",
        "Appliance and mattress revalorization",
        "Donation photos on request",
      ],
      zonesKicker: "Service areas",
      zonesH2: "All of French-speaking Switzerland.",
      zonesBody: "Mopper operates in French-speaking Switzerland, particularly in the canton of Vaud — Lausanne, Morges, Renens, Bussigny, Nyon, Vevey, Montreux, Yverdon-les-Bains, Geneva and Fribourg. Our team can also serve Neuchâtel, Valais and Jura on request.",
      trustH2: "Why our customers trust us.",
      trustItems: ["French-speaking Switzerland", "Free quote", "WhatsApp direct", "Fast intervention", "Combinable services", "Pro team"],
      trustNote: "4.4/5 · +250 happy customers",
      finalH2: ["A space to empty, furniture to transport, a move to organize?", "Mopper takes care of it."],
    },
    qf: {
      sectionService: "01 · Service",
      sectionContact: "02 · Your contact info",
      sectionSummary: "03 · Summary",
      pickService: "Which service are you interested in?",
      pickServiceSub: "Pick your main need. You can specify everything else right after.",
      contactTitle: "Almost done.",
      contactSub: "A few details to send you a tailored quote.",
      summaryTitle: "Confirm and send.",
      summarySub: "We'll get back to you with a quote within 24h.",
      youAre: "You are",
      when: "When would you like to be served?",
      precision: "Any precision?",
      precisionHint: "Photos, constraints, difficult access, floor without lift…",
      precisionPh: "Describe anything that could help us estimate better.",
      changeService: "Change service",
      thanks: "Thanks, noted!",
      thanksSub: "A confirmation email is on its way to",
      thanksSub2: ". Our team will get back to you with a quote within 24h.",
      thanksHome: "Back to home",
      firstName: "First name",
      lastName: "Last name",
      companyName: "Company name",
      companyNamePh: "e.g. Mopper Ltd",
      phone: "Phone",
      email: "Email",
      address: "Intervention address",
      addressPh: "Street, number, additional info",
      postal: "Postal code",
      date: "Preferred date",
      dateHint: "An approximate date is enough, we'll adapt.",
      datePh: "Select a date",
      choosePh: "Select…",
      slot: "Preferred time slot",
      noteLabel: "Note",
      summaryService: "Service",
      summaryProfile: "Profile",
      summaryContact: "Contact",
      summaryAddress: "Address",
      summaryDate: "Date",
      summarySlot: "Slot",
      summaryWhen: "When",
      services: {
        move: { label: "Moving", cta: "Request my moving" },
        clear: { label: "Clear-out", cta: "Request my clear-out" },
        declutter: { label: "Decluttering", cta: "Request guidance" },
        organize: { label: "Home Organizing", cta: "Request organizing" },
        clean: { label: "Cleaning", cta: "Book my cleaning" },
        recycle: { label: "Recycling", cta: "Request pickup" },
        assembly: { label: "Furniture assembly", cta: "Request assembly" },
        transport: { label: "Furniture transport", cta: "Request transport" },
        full: { label: "Full package", cta: "Delegate everything to Mopper" },
      },
      slots: {
        morning: { label: "Morning", hint: "8am–12pm" },
        afternoon: { label: "Afternoon", hint: "12–5pm" },
        evening: { label: "Evening", hint: "5–8pm" },
        any: { label: "Any time", hint: "We adapt" },
      },
      urgency: {
        asap: { label: "As soon as possible", hint: "Within 48–72h" },
        soon: { label: "This week" },
        month: { label: "This month" },
        flex: { label: "Flexible" },
      },
      clientType: { particulier: "Residential", professionnel: "Professional" },
      titles: {
        move: "Tell us about your move.",
        transport: "What needs to be transported?",
        clear: "Which space needs to be emptied?",
        declutter: "We guide you through sorting.",
        organize: "Which room would you like to organize?",
        clean: "Details of your cleaning.",
        recycle: "What needs to be picked up and recycled?",
        assembly: "Assembling or dismantling?",
        full: "Build your Mopper offer.",
        default: "Details of your request.",
      },
      subs: {
        move: "A few key details to quickly estimate your move.",
        transport: "We adapt the vehicle and crew to your furniture.",
        clear: "We empty, sort, dispose. Give us the right info.",
        declutter: "No pressure, at your pace. We help you decide.",
        organize: "Pretty, practical, sustainable.",
        clean: "For an accurate quote, tell us what should shine.",
        recycle: "We direct to the right channel: donation, recycling, landfill.",
        assembly: "IKEA and other brands. Pre or post-move.",
        full: "Check whatever you need — we'll sync.",
      },
      f: {
        addrFrom: "Pickup address",
        addrTo: "Destination address",
        addrPh: "Street, city, postal code",
        rooms: "Rooms",
        surface: "Surface (m²)",
        floorFrom: "Floor (from)",
        floorTo: "Floor (to)",
        liftFrom: "Lift (from)",
        liftTo: "Lift (to)",
        addNeeds: "Additional needs",
        selectMultiple: "You can select several options.",
        specifyOptional: "Specify if needed (optional)",
        needBoxes: "Boxes provided",
        needDis: "Furniture disassembly / reassembly",
        needCleaning: "Cleaning of the old home",
        itemTr: "Item to transport",
        itemTrPh: "3-seater sofa, wardrobe, fridge…",
        dim: "Approximate dimensions",
        dimPh: "L × W × H in cm",
        depart: "From",
        arrive: "To",
        addr: "Address",
        disReq: "Disassembly / reassembly required?",
        spaceType: "Space type",
        spaces: { cave: "Cellar", grenier: "Attic", garage: "Garage", appartement: "Apartment", maison: "House", bureau: "Office / premises" },
        surfApprox: "Approximate surface (m²)",
        volume: "Volume (m³)",
        volApprox: "Approximate volume (m³)",
        floor: "Floor",
        liftAvail: "Lift available?",
        precisions: "Precisions",
        heavy: "Heavy items (piano, safe…)",
        cleanAfter: "Cleaning after clear-out",
        targetRoom: "Target room(s)",
        targetRoomPh: "Wardrobe, kitchen, garage…",
        goal: "Main goal",
        goalPh: "Reduce volume, free up space, start fresh…",
        roomOrg: "Room to organize",
        roomOrgPh: "Wardrobe, kitchen, laundry…",
        goalOrg: "Goal",
        goalOrgPh: "Aesthetics, space, accessibility…",
        needStorage: "Purchase of storage solutions",
        needDecFirst: "Decluttering first",
        cleanType: "Cleaning type",
        cleanTypes: { fin_bail: "End-of-lease", apres_demenagement: "After moving", apres_debarras: "After clear-out", regulier: "Regular" },
        nbRooms: "Number of rooms",
        options: "Options",
        winInc: "Windows included",
        appInc: "Appliances (oven, fridge…)",
        finalLease: "Presence at handover",
        recItems: "Type of items to recycle",
        recItemsPh: "Appliances, furniture, electronics, cardboard…",
        asbKind: "Type of intervention",
        asbKinds: { assembly: "Assembly", disassembly: "Disassembly", both: "Disassembly + reassembly" },
        brand: "Main brand",
        brands: { ikea: "IKEA", autre: "Other", mix: "Mixed" },
        nbFurn: "Number of items",
        fullSubsLabel: "Which services would you like to include?",
        fullHint: "We combine everything in one quote.",
      },
      contactFields: { firstPh: "", phonePh: "+41 79 000 00 00", emailPh: "you@email.ch", postalPh: "1003" },
      keys: {
        depart: "From", arrive: "To", rooms: "Rooms", surface: "Surface", volume: "Volume", floorFrom: "Floor (from)", floorTo: "Floor (to)",
        options: "Options", boxes: "Boxes", disassembly: "Disassembly", cleaning: "Cleaning", cleanAfter: "Cleaning after", heavy: "Heavy items",
        item: "Item", dim: "Dimensions", space: "Space", floor: "Floor", precisions: "Precisions", room: "Room", goal: "Goal",
        purchases: "Storage purchase", decFirst: "Declutter first", type: "Type", win: "Windows", appl: "Appliances", finalLease: "Handover",
        items: "Items", brand: "Brand", furn: "Items", included: "Services included",
      },
    },
    job: {
      notFound: "Job not found",
      notFoundSub: "This job is no longer available.",
      seeAll: "View all jobs",
      openBadge: "Applications open",
      closedBadge: "Applications closed",
      urgent: "Urgent",
      positions: (n) => `${n} positions`,
      start: "Start:",
      applyOffer: "Apply for this job",
      applyShort: "Apply",
      description: "Description",
      missions: "Main missions",
      profile: "Profile sought",
      skills: "Skills",
      benefits: "Benefits offered",
      additionalInfo: "Additional information",
      brief: "At a glance",
      contract: "Contract",
      rate: "Rate",
      place: "Location",
      startK: "Start",
      deadline: "Application deadline",
      schedule: "Schedule",
      weekend: "Weekend",
      evening: "Evening",
      experience: "Experience",
      education: "Education",
      license: "Driving license",
      languages: "Languages",
      application: "Application",
      applyHere: "Apply for this job",
      mandatory: "All fields marked with an asterisk are required. Reply within 5 business days.",
      sent: "Application sent!",
      sentSub: "Thanks for your interest. We'll get back to you very quickly.",
      seeOtherJobs: "View other jobs",
      msgCoverLetter: "Message / cover letter",
      msgPh: "Introduce yourself in a few lines.",
      docsRequested: "Requested documents",
      docsHint: "Paste a public link (Google Drive, Dropbox, OneDrive). Tip: enable sharing before copying the link.",
      questions: "Questions",
      customDocLabel: "Custom document",
      consent: "I agree that Mopper processes my information for the purposes of this recruitment. Stored for 12 months max, deletion on request to hello@gomopper.ch.",
      submitApp: "Send my application",
      docRequired: (l) => `The document ${l} is required (public link).`,
      qRequired: (t) => `The question "${t}" is required.`,
      choose: "— choose —",
      docLabels: { cv: "CV", motivation: "Cover letter", certificates: "Work certificates", driving_license: "Driving license", work_permit: "Work permit", diplomas: "Diplomas", custom: "Document" },
      featured: "Featured",
      city: "City",
    },
    booking: {
      mopperBrand: "Mopper · Quote",
      operators: "Operators",
      estimateTTC: "Estimate incl. VAT",
      vatLine: "Swiss VAT 8.1% included. No commitment.",
      custom: "Custom",
      personsShort: "ppl",
      personsLabel: (n) => `${n} ppl.`,
    },
    espace: {
      quote: "Quote",
      quoteIn24h: "Quote within 24h",
      reviewing: "Under review",
      quoted: "Quote sent",
      confirmed: "Confirmed",
      completed: "Completed",
      cancelled: "Cancelled",
    },
    wa: {
      reply: "Reply within minutes",
      greet: "Hi 👋 A question or need a quote?",
      sub: "Send us a few photos or describe your needs on WhatsApp.",
      start: "Start the conversation",
      close: "Close",
      open: "Open WhatsApp chat",
      closeAria: "Close WhatsApp chat",
    },
    days: ["M", "T", "W", "T", "F", "S", "S"],
  },
};

export function useExtraT() {
  const { lang } = useI18n();
  return EXTRA[lang] || EXTRA.fr;
}

export default EXTRA;
